import {
  assertValidCalculationDefinition,
  evaluateCalculation,
} from './fieldCalculations';
import { resolveUnifiedFieldValue } from './fieldValueResolver';
import {
  activeRoleIdForState,
  quotePermissionContext,
  resolveDisposableFieldGrant,
} from './permissionPolicy';
import { quoteFieldAvailabilityForItem } from './quoteFieldAvailability';
import type {
  CalculationOperandValueType,
  CpqState,
  FrozenCalculationFact,
  FrozenCalculationFactorFact,
  Quote,
  QuoteCalculationBinding,
  QuoteFieldPlacement,
  QuoteFieldSelectionRecord,
  QuoteItem,
} from './types';
import type { UnifiedFieldValuePort } from './unifiedFieldPort';
import { createUnifiedFieldRegistry } from './unifiedFieldRegistry';

export interface CalculationPublishFailure {
  fieldKey: string;
  scopeId?: string;
  itemId?: string;
  placement: QuoteFieldSelectionRecord['placement'];
  reason: string;
  missingFieldKeys?: string[];
}

export class CalculationPublishError extends Error {
  readonly failures: CalculationPublishFailure[];

  constructor(failures: CalculationPublishFailure[]) {
    super(
      `计算字段无法冻结：${failures
        .map(
          (failure) =>
            `${failure.fieldKey}${failure.scopeId ? `/${failure.scopeId}` : ''}：${failure.reason}`,
        )
        .join('；')}`,
    );
    this.name = 'CalculationPublishError';
    this.failures = failures;
  }
}

function selectionsForQuote(quote: Quote): QuoteFieldSelectionRecord[] {
  const byIdentity = new Map<string, QuoteFieldSelectionRecord>();
  for (const selection of [
    ...quote.customColumns.map((fieldKey) => ({
      fieldKey,
      placement: 'quote-line' as const,
    })),
    ...(quote.fieldSelections ?? []),
  ])
    byIdentity.set(`${selection.placement}:${selection.fieldKey}`, selection);
  return [...byIdentity.values()];
}

function numericType(value: unknown): value is CalculationOperandValueType {
  return value === 'number' || value === 'money' || value === 'percent';
}

/** Bind the currently selected roots and every calculated dependency to the
 * latest directory definition. This is called only for a new selection/new
 * quote or the explicit apply-latest command. */
export function bindLatestSelectedCalculations(
  state: CpqState,
  sourceQuote: Quote,
): Quote {
  const quote = structuredClone(sourceQuote);
  const definitions = new Map(
    state.catalog.quoteFieldDefinitions.map((field) => [field.key, field]),
  );
  const descriptors = createUnifiedFieldRegistry(
    state.catalog.quoteFieldDefinitions,
  ).map((descriptor) => ({
    key: descriptor.definition.key,
    label: descriptor.definition.label,
    owner: descriptor.owner,
    type: descriptor.definition.type,
    enabled: descriptor.definition.enabled,
    calculation: descriptor.definition.calculation,
    factorApplicability: descriptor.binding.factorApplicability,
  }));
  const bindings: Record<string, QuoteCalculationBinding> = {};
  const bind = (fieldKey: string, stack: readonly string[]) => {
    if (bindings[fieldKey]) return;
    if (stack.includes(fieldKey))
      throw new CalculationPublishError([
        {
          fieldKey,
          placement: 'quote-line',
          reason: '计算依赖循环',
        },
      ]);
    const field = definitions.get(fieldKey);
    if (!field?.calculation) return;
    try {
      assertValidCalculationDefinition(field.calculation, {
        fields: descriptors,
        // A quote-line has an explicit quote/version/item/relation context;
        // it may resolve approved upstream object facts through that context.
        // Entity formulas remain same-owner and cardinality checked.
        allowSystemCrossOwner:
          field.systemLocked || field.calculation.owner === 'quote-line',
        currentFieldKey: field.key,
      });
    } catch (error) {
      throw new CalculationPublishError([
        {
          fieldKey,
          placement: 'quote-line',
          reason: error instanceof Error ? error.message : '计算定义无效',
        },
      ]);
    }
    const fieldTypes: Record<string, CalculationOperandValueType> = {};
    for (const operand of field.calculation.operands) {
      if (operand.kind !== 'field') continue;
      const dependency = definitions.get(operand.fieldKey);
      if (dependency && numericType(dependency.type))
        fieldTypes[operand.fieldKey] = dependency.type;
      if (dependency?.calculation) bind(dependency.key, [...stack, fieldKey]);
    }
    bindings[fieldKey] = {
      fieldKey,
      definitionVersion: field.calculation.definitionVersion,
      definition: structuredClone(field.calculation),
      fieldTypes,
    };
  };
  for (const selection of selectionsForQuote(quote))
    bind(selection.fieldKey, []);
  quote.calculationBindings = bindings;
  return quote;
}

function bindingsForQuote(
  quote: Quote,
): Record<string, QuoteCalculationBinding> {
  if (quote.calculationBindings) return quote.calculationBindings;
  // Compatibility for drafts created before quote-level bindings existed:
  // only their already-frozen selected column definitions are accepted. No
  // directory calculation is consulted here, so a later edit cannot switch
  // the draft silently.
  return Object.fromEntries(
    (quote.customColumnSnapshot ?? []).flatMap((snapshot) =>
      snapshot.calculation
        ? [
            [
              snapshot.key,
              {
                fieldKey: snapshot.key,
                definitionVersion: snapshot.calculation.definitionVersion,
                definition: structuredClone(snapshot.calculation),
                fieldTypes: {},
              } satisfies QuoteCalculationBinding,
            ],
          ]
        : [],
    ),
  );
}

interface CalculationTarget {
  item: QuoteItem;
  scopeId: string;
  entityId: string;
  owner: QuoteCalculationBinding['definition']['owner'];
  relationId?: string;
}

function applicableTargets(
  quote: Quote,
  placement: QuoteFieldPlacement,
  binding: QuoteCalculationBinding,
): CalculationTarget[] {
  const owner = binding.definition.owner;
  if (placement === 'package-summary' && owner === 'quote-line') {
    const seen = new Set<string>();
    return quote.items.flatMap((item) => {
      if (!item.packageId || seen.has(item.packageId)) return [];
      seen.add(item.packageId);
      return [
        {
          item,
          scopeId: item.packageId,
          entityId: item.itemId,
          owner,
        },
      ];
    });
  }
  const candidates: CalculationTarget[] = [];
  for (const item of quote.items) {
    if (owner === 'quote-line') {
      candidates.push({
        item,
        scopeId: item.itemId,
        entityId: item.itemId,
        owner,
      });
      continue;
    }
    if (owner === 'device') {
      candidates.push({
        item,
        scopeId: item.itemId,
        entityId: item.deviceId,
        owner,
      });
      continue;
    }
    if (owner === 'package' && item.packageId) {
      candidates.push({
        item,
        scopeId: item.packageId,
        entityId: item.packageId,
        owner,
        relationId: item.packageRelationId,
      });
      continue;
    }
    if (owner === 'plan' && item.planId) {
      candidates.push({
        item,
        scopeId: item.planId,
        entityId: item.planId,
        owner,
        relationId: item.packageRelationId,
      });
      continue;
    }
    if (owner === 'customer' && quote.customer.customerId) {
      candidates.push({
        item,
        scopeId: quote.customer.customerId,
        entityId: quote.customer.customerId,
        owner,
      });
    }
  }
  if (placement === 'quote-line') return candidates;
  if (placement === 'quote-header') return candidates.slice(0, 1);
  const seen = new Set<string>();
  return candidates.filter(({ scopeId }) => {
    if (seen.has(scopeId)) return false;
    seen.add(scopeId);
    return true;
  });
}

function systemCalculationApplicable(
  fieldKey: string,
  target: CalculationTarget,
): boolean {
  const item = target.item;
  if (fieldKey === 'rental_subtotal')
    return item.pricingBaseSnapshot?.kind === 'rental';
  if (fieldKey === 'dynamic_cost_subtotal')
    return item.deviceFieldSnapshot !== undefined;
  if (
    fieldKey === 'scheme_product_price' ||
    fieldKey === 'scheme_product_subtotal'
  )
    return Boolean(item.planId && item.packageItemPriceSnapshot !== undefined);
  if (
    fieldKey === 'scheme_package_price' ||
    fieldKey === 'package_original_price_tax13' ||
    fieldKey === 'package_discount_quote_tax13'
  )
    return Boolean(item.packageId);
  return item.pricingBaseSnapshot !== undefined;
}

function targetContext(
  quote: Quote,
  target: CalculationTarget,
  fieldValuePort: UnifiedFieldValuePort,
) {
  return {
    owner: target.owner,
    entityId: target.entityId,
    quoteId: quote.quoteId,
    quoteVersion: quote.version,
    relationId: target.relationId,
    scopeId: target.scopeId,
    item: target.item,
    fieldValuePort,
  };
}

function permissionAllows(
  state: CpqState,
  quote: Quote,
  fieldId: string,
): boolean {
  const roleId = activeRoleIdForState(state);
  return Boolean(
    roleId &&
      resolveDisposableFieldGrant(
        state,
        roleId,
        fieldId,
        'view',
        'draft',
        quotePermissionContext(state, quote),
      ),
  );
}

function freezeOne(
  state: CpqState,
  quote: Quote,
  target: CalculationTarget,
  rootKey: string,
  placement: QuoteFieldSelectionRecord['placement'],
  bindings: Record<string, QuoteCalculationBinding>,
  fieldValuePort: UnifiedFieldValuePort,
): FrozenCalculationFact | CalculationPublishFailure {
  const allFactors: FrozenCalculationFactorFact[] = [];
  const addFactor = (fact: FrozenCalculationFactorFact) => {
    const existing = allFactors.find(
      (candidate) =>
        candidate.fieldKey === fact.fieldKey &&
        candidate.scopeId === fact.scopeId &&
        candidate.itemId === fact.itemId &&
        candidate.relationId === fact.relationId,
    );
    if (existing && existing.value !== fact.value)
      throw new Error(`同一冻结因子身份值冲突：${fact.fieldKey}`);
    if (!existing) allFactors.push(fact);
  };
  const directFactor = (
    fieldKey: string,
    value: number,
    valueType: CalculationOperandValueType,
  ): FrozenCalculationFactorFact => ({
    fieldKey,
    value,
    valueType,
    quoteId: quote.quoteId,
    quoteVersion: quote.version,
    owner: target.owner,
    scopeId: target.scopeId,
    itemId: target.item.itemId,
    ...(target.relationId ? { relationId: target.relationId } : {}),
  });
  const evaluateBound = (
    fieldKey: string,
    stack: readonly string[],
  ):
    | { ok: true; value: number }
    | { ok: false; reason: string; missing?: string[] } => {
    if (stack.includes(fieldKey)) return { ok: false, reason: '计算依赖循环' };
    const binding = bindings[fieldKey];
    if (!binding) return { ok: false, reason: `计算定义绑定缺失：${fieldKey}` };
    if (!permissionAllows(state, quote, `definition:${fieldKey}`))
      return { ok: false, reason: `计算字段权限不足：${fieldKey}` };
    const context = targetContext(quote, target, fieldValuePort);
    if (binding.definition.resolverRef) {
      const resolved = resolveUnifiedFieldValue(state, fieldKey, context);
      if (resolved.status !== 'value' || typeof resolved.value !== 'number')
        return {
          ok: false,
          reason: resolved.error ?? '系统计算上下文不可用',
          missing: resolved.missingFieldKeys,
        };
      // Device rows expose package_discounted_subtotal as the same immutable
      // final line amount used by the quote totals.  That projection is not
      // the package formula (there is no package_item_price factor), so the
      // resolver intentionally returns the value without package audit
      // factors.  Keep the frozen result for the quote-line snapshot instead
      // of rejecting an otherwise valid device quote during workflow import.
      const directDeviceSubtotalProjection =
        rootKey === 'package_discounted_subtotal' &&
        target.item.sourceType === 'device';
      if (!resolved.factorFacts && !directDeviceSubtotalProjection)
        return { ok: false, reason: '系统计算未返回可审计因子事实' };
      for (const fact of resolved.factorFacts ?? []) addFactor(fact);
      return { ok: true, value: resolved.value };
    }
    const values: Record<string, number> = {};
    const fieldTypes = { ...binding.fieldTypes };
    for (const operand of binding.definition.operands) {
      if (operand.kind !== 'field') continue;
      const dependencyBinding = bindings[operand.fieldKey];
      if (dependencyBinding) {
        const upstream = evaluateBound(operand.fieldKey, [...stack, fieldKey]);
        if (!upstream.ok) return upstream;
        values[operand.fieldKey] = upstream.value;
        addFactor(
          directFactor(
            operand.fieldKey,
            upstream.value,
            dependencyBinding.definition.resultType,
          ),
        );
        fieldTypes[operand.fieldKey] = dependencyBinding.definition.resultType;
        continue;
      }
      if (!permissionAllows(state, quote, `definition:${operand.fieldKey}`))
        return { ok: false, reason: `依赖字段权限不足：${operand.fieldKey}` };
      const factor = resolveUnifiedFieldValue(state, operand.fieldKey, context);
      if (factor.status !== 'value' || typeof factor.value !== 'number')
        return {
          ok: false,
          reason: factor.error ?? `依赖字段缺失：${operand.fieldKey}`,
          missing: factor.missingFieldKeys ?? [operand.fieldKey],
        };
      values[operand.fieldKey] = factor.value;
      if (!numericType(factor.type))
        return {
          ok: false,
          reason: `依赖字段类型不可计算：${operand.fieldKey}`,
        };
      addFactor(directFactor(operand.fieldKey, factor.value, factor.type));
    }
    const evaluated = evaluateCalculation(binding.definition, values, {
      fieldTypes,
    });
    return evaluated.ok && typeof evaluated.value === 'number'
      ? { ok: true, value: evaluated.value }
      : {
          ok: false,
          reason: evaluated.error ?? '计算失败',
          missing: evaluated.missingFieldKeys,
        };
  };

  const binding = bindings[rootKey];
  if (!binding)
    return {
      fieldKey: rootKey,
      scopeId: target.scopeId,
      placement,
      reason: '计算定义绑定缺失',
    };
  const evaluated = evaluateBound(rootKey, []);
  if (!evaluated.ok)
    return {
      fieldKey: rootKey,
      scopeId: target.scopeId,
      itemId: target.item.itemId,
      placement,
      reason: evaluated.reason,
      missingFieldKeys: evaluated.missing,
    };
  return {
    fieldKey: rootKey,
    definitionVersion: binding.definitionVersion,
    definition: structuredClone(binding.definition),
    factors: allFactors,
    result: evaluated.value,
    quoteId: quote.quoteId,
    quoteVersion: quote.version,
    scopeId: target.scopeId,
    itemId: target.item.itemId,
    placement,
  };
}

/** Validate and freeze the complete selected/applicable calculation package
 * on a clone. Any failure throws before the caller replaces state. */
export function freezeSelectedCalculationFacts(
  state: CpqState,
  sourceQuote: Quote,
  fieldValuePort: UnifiedFieldValuePort,
): Quote {
  const quote = structuredClone(sourceQuote);
  const bindings = bindingsForQuote(quote);
  const failures: CalculationPublishFailure[] = [];
  const selections = selectionsForQuote(quote);
  const selectedCalculationKeys = new Set(
    selections
      .map((selection) => selection.fieldKey)
      .filter((fieldKey) => Boolean(bindings[fieldKey])),
  );
  for (const item of quote.items) {
    if (!item.calculationSnapshots) continue;
    item.calculationSnapshots = Object.fromEntries(
      Object.entries(item.calculationSnapshots).filter(([fieldKey]) =>
        selectedCalculationKeys.has(fieldKey),
      ),
    );
  }
  for (const selection of selections) {
    const binding = bindings[selection.fieldKey];
    const frozenColumn = quote.customColumnSnapshot?.find(
      (snapshot) =>
        snapshot.key === selection.fieldKey &&
        (snapshot.placement ?? 'quote-line') === selection.placement,
    );
    if (!binding && !frozenColumn?.calculation) continue;
    if (!binding) {
      failures.push({
        fieldKey: selection.fieldKey,
        placement: selection.placement,
        reason: '计算定义绑定缺失',
      });
      continue;
    }
    for (const item of quote.items) {
      if (item.calculationSnapshots)
        delete item.calculationSnapshots[selection.fieldKey];
    }
    const targets = applicableTargets(quote, selection.placement, binding);
    for (const target of targets) {
      if (
        binding.definition.resolverRef &&
        !systemCalculationApplicable(selection.fieldKey, target)
      )
        continue;
      if (
        selection.placement === 'quote-line' &&
        quoteFieldAvailabilityForItem(selection.fieldKey, target.item) !==
          'available'
      )
        continue;
      const result = freezeOne(
        state,
        quote,
        target,
        selection.fieldKey,
        selection.placement,
        bindings,
        fieldValuePort,
      );
      if ('reason' in result) {
        failures.push(result);
        continue;
      }
      target.item.calculationSnapshots = {
        ...(target.item.calculationSnapshots ?? {}),
        [selection.fieldKey]: result,
      };
      if (frozenColumn) {
        frozenColumn.calculation = structuredClone(binding.definition);
        frozenColumn.formulaVersion = binding.definitionVersion;
        delete frozenColumn.calculationSnapshot;
      }
    }
  }
  if (failures.length) throw new CalculationPublishError(failures);
  quote.calculationBindings = structuredClone(bindings);
  return quote;
}

export function selectedCalculationKeys(quote: Quote): string[] {
  const bindings = bindingsForQuote(quote);
  return [
    ...new Set(
      selectionsForQuote(quote)
        .map((selection) => selection.fieldKey)
        .filter((fieldKey) => Boolean(bindings[fieldKey])),
    ),
  ];
}
